//
// ContentZoomer
//
// scalingDiv scales the children content, measures its un-scaled size
// trimmingDiv makes sure the content size is adjusted for, in the layout
//
// When the child changes size, be sure to change the key to force the
// module to re-evaluate the child's size
//

import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  useCallback,
  useEffect,
  useReducer,
  useRef,
} from 'react';

import { useTownAssets } from '../../stores';
import {
  displayJson,
  logError,
} from '../../utils';
import SvgColorImg from '../SvgColorImg';

const minScale = 0.06;
const maxScale = 5;

const initialState = {
  scale: 0,
  childSize: { height: 0, width: 0 },
  zoomedChildSize: { height: 0, width: 0 },
  frameSize: { height: 0, width: 0 },
};

const isPositiveFinite = (n) => (Number.isFinite(n) && (n > 0));

const stateReducer = (state, action) => {
  switch (action.type) {
    case 'setSizes': {
      const { childSize, frameSize } = action;

      if (!isPositiveFinite(childSize?.width)
          || !isPositiveFinite(childSize?.height)
          || !isPositiveFinite(frameSize?.width)
          || !isPositiveFinite(frameSize?.height)) {
        return state;
      }

      const widthScale = (frameSize.width / childSize.width) * 0.95;
      const heightScale = (frameSize.height / childSize.height) * 0.95;
      const candidate = Math.min(widthScale, heightScale);
      if (!isPositiveFinite(candidate)) return state;
      const newScale = +candidate.toFixed(2);
      return ({
        ...state,
        childSize,
        frameSize,
        scale: newScale,
        fitScale: newScale,
        zoomedChildSize: {
          width: childSize.width * newScale,
          height: childSize.height * newScale,
        },
      }); }

    case 'zoom': {
      if (!isPositiveFinite(state.scale)
          || !isPositiveFinite(state.childSize?.width)
          || !isPositiveFinite(state.childSize?.height)) {
        return state;
      }
      const multiplier = (action.deltaY > 0) ? 0.7 : (1.4285);

      const newScale = +Math.min(
        maxScale,
        Math.max(
          state.scale * multiplier,
          minScale,
        ),
      ).toFixed(2);

      return ({
        ...state,
        scale: newScale,
        zoomedChildSize: {
          width: state.childSize.width * newScale,
          height: state.childSize.height * newScale,
        },
      }); }

    case 'resetScale':
      if (!isPositiveFinite(state.fitScale)
          || !isPositiveFinite(state.childSize?.width)
          || !isPositiveFinite(state.childSize?.height)) {
        return state;
      }
      return ({
        ...state,
        scale: state.fitScale,
        zoomedChildSize: {
          width: state.childSize.width * state.fitScale,
          height: state.childSize.height * state.fitScale,
        },
      });

    default:
      logError(
        `ContentZoomer unknown action=${displayJson(action, 2)}`,
        'ContentZoomer.reducer.unknownAction',
        { actionType: action?.type },
      );
      return state;
  }
};

function ContentZoomer({
  className,
  // scale: initScale,
  children = null,
  checkerBackground = true,
  style,
  notifyNewScale,
  noScaling = false,
}) {
  const { translate } = useTownAssets().assets;
  const [state, dispatch] = useReducer(stateReducer, initialState);
  const frameDivRef = useRef(null);
  const scalingDivRef = useRef(null);

  // Tell the parent the scale has changed if needed
  //
  useEffect(
    () => {
      if (notifyNewScale) notifyNewScale(state.scale);
    },
    [notifyNewScale, state.scale],
  );

  const setSizes = useCallback(
    () => {
      if (!scalingDivRef.current || !frameDivRef.current) return;
      const childDiv = scalingDivRef.current.firstChild;
      if (!childDiv) return;

      // clientWidth/Height: layout px like the child's offsetWidth below, so the
      // fit ratio stays correct under the dashboard shell zoom
      const frameWidth = frameDivRef.current.clientWidth;
      const frameHeight = frameDivRef.current.clientHeight;
      if ((!frameWidth) || (!frameHeight)) return;

      // Measure the child's intrinsic (un-transformed) size. offsetWidth/Height
      // exist on HTMLElement; SVG elements expose clientWidth/Height instead;
      // anything else falls back to getBoundingClientRect.
      let width = (typeof childDiv.offsetWidth === 'number') ? childDiv.offsetWidth : 0;
      let height = (typeof childDiv.offsetHeight === 'number') ? childDiv.offsetHeight : 0;
      if ((!width) || (!height)) {
        width = childDiv.clientWidth || width;
        height = childDiv.clientHeight || height;
      }
      if ((!width) || (!height)) {
        const rect = childDiv.getBoundingClientRect?.();
        width = rect?.width || width;
        height = rect?.height || height;
      }
      if ((!width) || (!height)) return;

      dispatch({
        type: 'setSizes',
        childSize: {
          height: +height.toFixed(1),
          width: +width.toFixed(1),
        },
        frameSize: {
          width: frameWidth,
          height: frameHeight,
        },
      });
    },
    [],
  );

  useEffect(
    () => {
      if (scalingDivRef.current && frameDivRef.current) {
        setSizes();
      }
    },
    [setSizes],
  );

  // Re-measure when the scaling div's child arrives, swaps, or resizes. Callers used to
  // be required to bump a `key` whenever their child's size changed; this catches the
  // common case (child rendered asynchronously after first mount) automatically.
  useEffect(
    () => {
      const scalingDiv = scalingDivRef.current;
      if (!scalingDiv) return undefined;

      let resizeObserver;
      const observeChild = () => {
        if (resizeObserver) resizeObserver.disconnect();
        const child = scalingDiv.firstChild;
        if (!child || (typeof ResizeObserver === 'undefined')) return;
        if (child.nodeType !== 1) return; // only Element nodes
        resizeObserver = new ResizeObserver(() => setSizes());
        resizeObserver.observe(child);
      };

      const mutationObserver = (typeof MutationObserver !== 'undefined')
        ? new MutationObserver(() => {
            setSizes();
            observeChild();
          })
        : undefined;
      mutationObserver?.observe(scalingDiv, { childList: true });
      observeChild();

      return () => {
        mutationObserver?.disconnect();
        resizeObserver?.disconnect();
      };
    },
    [setSizes],
  );

  useEffect(
    () => {
      if (!noScaling) {
        const handleWheel = (event) => {
          if (event.altKey) {
          // Only when Alt key is pressed
            event.preventDefault();

            const frameDiv = frameDivRef.current;
            const scalingDiv = scalingDivRef.current;

            // Calculate mouse position within the scalingDiv
            const mouse = {
              x: event.clientX + frameDiv.scrollLeft - scalingDiv.getBoundingClientRect().left,
              y: event.clientY + frameDiv.scrollTop - scalingDiv.getBoundingClientRect().top,
            };

            const { height, width } = scalingDiv.getBoundingClientRect();

            console.log(`Zoom around ${JSON.stringify(mouse)}, width=${width} height=${height}`);

            dispatch({
              type: 'zoom',
              deltaY: event.deltaY,
            });

            // // Calculate the new mouse position after zoom
            // const newMouseX = (mouseX * state.scale) / (state.scale - (e.deltaY * zoomSpeed));
            // const newMouseY = (mouseY * state.scale) / (state.scale - (e.deltaY * zoomSpeed));

          // // Set the scroll position based on the new mouse position
          // div.scrollLeft += (newMouseX - mouseX);
          // div.scrollTop += (newMouseY - mouseY);
          }
        };

        const div = frameDivRef.current;

        div.addEventListener('wheel', handleWheel, { passive: false });

        // Cleanup listener on component unmount
        return () => {
          div.removeEventListener('wheel', handleWheel);
        };
      }
      return undefined;
    },
    [state.scale, noScaling],
  );

  const resetScale = useCallback(
    () => {
      dispatch({ type: 'resetScale' });
    },
    [],
  );

  return (
    <>
      <div
        id="frameDiv"
        ref={frameDivRef}
        className={classNames(
          'overflow-auto flex relative',
          { 'bg-checkers bg-[size:13px]': checkerBackground },
          className,
        )}
        style={style}
      >
        <div
          id="trimmingDiv"
          className="m-auto shrink-0"
          style={{
            width: `${state.zoomedChildSize.width}px`,
            height: `${state.zoomedChildSize.height}px`,
            overflow: 'hidden',
          }}
        >
          <div
            id="scalingDiv"
            className="shrink-0"
            style={{
              transform: `scale(${state.scale})`,
              transformOrigin: 'top left',
            }}
            ref={scalingDivRef}
          >
            {children}
          </div>
        </div>
      </div>
      {((!noScaling) && children)
      && (
        <>
          <span
            id="controls"
            className="absolute left-2 bottom-2 text-white/60 mix-blend-difference"
          >
            {`${translate('scale', undefined, undefined, false)} x${state.scale}`}
          </span>
          <span
            className="absolute left-1/2 -translate-x-1/2 bottom-2 text-white/60 mix-blend-difference"
          >
            {`(${translate('altWheelToZoom', 'statements')})`}
          </span>
          <button
            type="button"
            onClick={resetScale}
          >
            <SvgColorImg
              src="/images/svg/gui/expand.svg"
              className="w-6 h-6 right-2 bottom-2 mix-blend-difference opacity-50 fill-white"
              imgColorAsCssFill
              absolute
            />
          </button>
        </>
      )}
    </>
  );
}

ContentZoomer.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  checkerBackground: PropTypes.bool,
  style: PropTypes.shape({}),
  notifyNewScale: PropTypes.func,
  noScaling: PropTypes.bool,
};

export default ContentZoomer;
